import { forwardRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@heroui/react/textfield';
import { Input as HeroInput } from '@heroui/react/input';
import { InputGroup } from '@heroui/react/input-group';
import { TextArea as HeroTextArea } from '@heroui/react/textarea';
import { Label } from '@heroui/react/label';
import { Description } from '@heroui/react/description';
import { FieldError } from '@heroui/react/field-error';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useFieldLabelling } from './field-context';

/**
 * Text input — HeroUI's TextField stack.
 *
 * TextField wires Label / Description / FieldError together, so labels are
 * associated, hints and errors are announced via aria-describedby, and the
 * invalid state is communicated properly. Icons use InputGroup's Prefix /
 * Suffix slots rather than absolute positioning.
 */
export interface InputProps {
  /** Icon rendered inside the field, at the start. */
  startIcon?: ReactNode;
  /** Element rendered inside the field, at the end. */
  endIcon?: ReactNode;
  label?: ReactNode;
  /** Error message. Also puts the control into its invalid state. */
  error?: ReactNode;
  /** Helper text under the control. */
  hint?: ReactNode;
  hasError?: boolean;
  /** Adds a show/hide toggle on password inputs. */
  showPasswordToggle?: boolean;
  className?: string;
  type?: string;
  value?: string | number;
  defaultValue?: string | number;
  /** Native tooltip text. */
  title?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  id?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  maxLength?: number;
  minLength?: number;
  /** id of a `<datalist>` supplying suggestions. */
  list?: string;
  inputMode?:
    | 'text'
    | 'numeric'
    | 'decimal'
    | 'tel'
    | 'email'
    | 'url'
    | 'search';
  /**
   * Forces text direction on the field itself, independent of the page.
   *
   * Needed for values that are always Latin/numeric regardless of UI language —
   * tooth numbers, shade codes, phone numbers — which an RTL page would
   * otherwise reorder into nonsense.
   */
  dir?: 'ltr' | 'rtl' | 'auto';
  /**
   * For a field labelled by its surroundings rather than a `label` — a cell
   * in an editable grid is named by its column header and its row.
   */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      startIcon,
      endIcon,
      label,
      error,
      hint,
      hasError,
      showPasswordToggle = false,
      type = 'text',
      required,
      disabled,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const resolvedType = isPassword && showPassword ? 'text' : type;
    const invalid = hasError || !!error;
    const inherited = useFieldLabelling({ label, ...props });

    /*
      Reachable by keyboard. It was `tabIndex={-1}`, which is defensible for
      a decorative adornment but wrong here: revealing what you typed is a
      real action, and on a sign-in form it is the one a keyboard user most
      often wants after a failed attempt. It sits after the input in tab
      order, so it costs a Tab only to someone who wants it.
    */
    const toggle = isPassword && showPasswordToggle && (
      <button
        type="button"
        onClick={() => setShowPassword((v) => !v)}
        aria-label={showPassword ? t('auth.pw.hide') : t('auth.pw.show')}
        aria-pressed={showPassword}
        className="rounded-sm text-muted transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {showPassword ? (
          <EyeOff className="size-4" />
        ) : (
          <Eye className="size-4" />
        )}
      </button>
    );
    const suffix = toggle || endIcon;

    return (
      <TextField
        isRequired={required}
        isDisabled={disabled}
        isInvalid={invalid}
        defaultValue={
          defaultValue !== undefined ? String(defaultValue) : undefined
        }
        className={cn('flex w-full flex-col gap-1.5', className)}
      >
        {label && <Label>{label}</Label>}

        {startIcon || suffix ? (
          <InputGroup>
            {startIcon && <InputGroup.Prefix>{startIcon}</InputGroup.Prefix>}
            <InputGroup.Input
              ref={ref}
              type={resolvedType}
              value={value}
              onChange={onChange}
              {...inherited}
              {...props}
            />
            {suffix && <InputGroup.Suffix>{suffix}</InputGroup.Suffix>}
          </InputGroup>
        ) : (
          <HeroInput
            ref={ref}
            type={resolvedType}
            value={value}
            onChange={onChange}
            {...inherited}
            {...props}
          />
        )}

        {hint && !error && <Description>{hint}</Description>}
        {error && <FieldError>{error}</FieldError>}
      </TextField>
    );
  },
);
Input.displayName = 'Input';

export interface TextareaProps {
  label?: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
  hasError?: boolean;
  rows?: number;
  className?: string;
  /** Classes for the textarea itself; `className` styles the field wrapper. */
  inputClassName?: string;
  value?: string | number;
  defaultValue?: string | number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  id?: string;
  maxLength?: number;
  /**
   * Text direction. Needed wherever a field's content has a direction of its
   * own regardless of the page — an Arabic legal document inside an English
   * dashboard, or a URL inside an Arabic one.
   */
  dir?: 'ltr' | 'rtl' | 'auto';
  /** For a control whose label is supplied by the surrounding layout. */
  'aria-label'?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** For a composer that sends on Enter. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** For a composer that accepts a pasted image. */
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  /** For a composer that follows the caret (an "@" being typed). */
  onSelect?: (e: React.SyntheticEvent<HTMLTextAreaElement>) => void;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      inputClassName,
      label,
      error,
      hint,
      hasError,
      required,
      disabled,
      rows = 4,
      ...props
    },
    ref,
  ) => {
    const inherited = useFieldLabelling({ label, ...props });
    return (
      <TextField
        isRequired={required}
        isDisabled={disabled}
        isInvalid={hasError || !!error}
        className={cn('flex w-full flex-col gap-1.5', className)}
      >
        {label && <Label>{label}</Label>}
        <HeroTextArea
          ref={ref}
          rows={rows}
          className={inputClassName}
          {...inherited}
          {...props}
        />
        {hint && !error && <Description>{hint}</Description>}
        {error && <FieldError>{error}</FieldError>}
      </TextField>
    );
  },
);
Textarea.displayName = 'Textarea';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  hasError?: boolean;
}

/**
 * Native select, styled to match. Kept native because much of the app binds
 * it directly to react-hook-form `register()`. For rich option lists prefer
 * HeroUI's `Select` / `ComboBox` directly.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, hasError, children, ...props }, ref) => (
    <select
      ref={ref}
      aria-invalid={hasError || undefined}
      className={cn(
        'h-10 w-full cursor-pointer appearance-none rounded-md border bg-white px-3 pe-9',
        'text-body text-foreground transition-[border-color,box-shadow] duration-150',
        'border-neutral-200 hover:border-neutral-300',
        'focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/20',
        'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-muted',
        "bg-[url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.clinic/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23A0AEC0' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")]",
        'bg-[length:1rem] bg-no-repeat bg-[position:right_0.75rem_center] rtl:bg-[position:left_0.75rem_center]',
        hasError && 'border-danger focus:border-danger focus:ring-danger/20',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';
